import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  Icon,
} from "@assetwatch/ui";
import { Link } from "@tanstack/react-router";
import type { ReactElement, ReactNode } from "react";
import {
  type NavigationOrigin,
  type NavigationOriginType,
  toOriginSearch,
} from "../../lib/navigationOrigin";
import { buildWorkOrdersReturnSearch } from "../../lib/workOrdersSearch";
import { getWorkScopeSearch, type WorkSearch } from "../../lib/workSearch";
import { useNavigationOriginLabelQuery } from "../../queries/useNavigationOriginLabelQuery";
import { useWorkScope } from "../WorkScopeProvider";

/** Single back crumb above a detail page's title. */
export function BackCrumb({
  label,
  link,
}: {
  label: string;
  link: ReactElement;
}) {
  return (
    <Breadcrumb>
      <BreadcrumbList>
        <BreadcrumbItem>
          <BreadcrumbLink render={link}>
            <span className="flex min-w-0 items-center gap-(--spacing-4)">
              <Icon name="chevron-left" size="xs" tone="inherit" />
              <span className="min-w-0 truncate">{label}</span>
            </span>
          </BreadcrumbLink>
        </BreadcrumbItem>
      </BreadcrumbList>
    </Breadcrumb>
  );
}

export function useScopeSearch(): WorkSearch {
  const { selectedCustomer, selectedFacility } = useWorkScope();
  return selectedCustomer && selectedFacility
    ? getWorkScopeSearch(
        {
          id: selectedCustomer.id,
          facilities: { length: selectedCustomer.facilities.length },
        },
        { id: selectedFacility.id },
      )
    : {};
}

const GENERIC_ORIGIN_LABEL: Record<NavigationOriginType, string> = {
  asset: "Asset",
  request: "Request",
  workOrder: "Work Order",
};

/**
 * The back link of a page reached from another module: it replaces the
 * page's normal in-page back crumb (`fallback`) and returns to the
 * originating record with that module's list view restored. Without an
 * origin, or when the origin can't be shown to this viewer, the fallback
 * renders instead.
 */
export function ContextualBack({
  fallback,
  origin,
}: {
  fallback: ReactNode;
  origin: NavigationOrigin | undefined;
}) {
  const scopeSearch = useScopeSearch();
  const originLabel = useNavigationOriginLabelQuery(origin);
  if (!origin || originLabel.status === "unavailable") return fallback;

  /* @ds-deviation: the POC back control (poc-design app/screens/wo-detail.jsx
     and asset-detail.jsx, `back.label = navArg.from`) shows only the origin
     name, e.g. "Pump 1"; the accepted copy is "Back to {origin}", where a Work
     Order origin is its number and title ("Back to WO-45 · Replace hydraulic
     hose") — keep the accepted copy and update the POC. */
  const label = `Back to ${originLabel.label ?? GENERIC_ORIGIN_LABEL[origin.type]}`;

  return <BackCrumb label={label} link={originLink(origin, scopeSearch)} />;
}

function originLink(
  origin: NavigationOrigin,
  scopeSearch: WorkSearch,
): ReactElement {
  // Going back to `origin` restores the origin it was itself opened from.
  const parentSearch = origin.parent
    ? toOriginSearch(origin.parent, origin)
    : {};
  switch (origin.type) {
    case "request":
      return (
        <Link
          params={{ workRequestId: origin.id }}
          search={{ ...scopeSearch, ...origin.search, ...parentSearch }}
          to="/requests/$workRequestId"
        />
      );
    case "workOrder":
      return (
        <Link
          params={{ workOrderId: origin.id }}
          search={{
            ...buildWorkOrdersReturnSearch(origin.search, scopeSearch),
            ...parentSearch,
            workOrderFullPage: true,
          }}
          to="/work-orders/$workOrderId"
        />
      );
    case "asset":
      return (
        <Link
          params={{ assetId: origin.id }}
          search={{ ...scopeSearch, ...origin.search, ...parentSearch }}
          to="/assets/$assetId"
        />
      );
  }
}
